<script setup>
import { ref, reactive, nextTick } from "vue";
import { Modal } from "bootstrap";
import { usePaymentStore } from "@/stores/payment";

const emit = defineEmits(["saved"]);

const paymentStore = usePaymentStore();
const modalEl = ref(null);

// ─── ช่องทางชำระ ──────────────────────────────────────
const paymentMethods = [
  { label: "เงินสด", value: "cash", icon: "bi-cash" },
  { label: "โอนธนาคาร", value: "bank_transfer", icon: "bi-bank" },
  { label: "QR Payment", value: "qr_payment", icon: "bi-qr-code" },
  { label: "บัตรเครดิต", value: "credit_card", icon: "bi-credit-card" },
];

// ─── Form & Errors ────────────────────────────────────
const defaultForm = () => ({
  _id: null,
  paymentNo: "",
  invoiceNo: "",
  residentName: "",
  unitCode: "",
  building: "",
  paymentDate: "",
  amount: 0,
  method: "",
  bankName: "",
  referenceNo: "",
  status: "completed",
  receiptNo: "",
  note: "",
});

const form = reactive(defaultForm());
const errors = reactive({ paymentDate: "", amount: "", method: "" });

// ─── Helpers ──────────────────────────────────────────
const getModal = () => Modal.getOrCreateInstance(modalEl.value);

const resetForm = () => {
  Object.assign(form, defaultForm());
  Object.keys(errors).forEach((k) => (errors[k] = ""));
};

const validate = () => {
  errors.paymentDate = form.paymentDate ? "" : "กรุณาระบุวันที่รับชำระ";
  errors.amount =
    Number(form.amount) > 0 ? "" : "กรุณาระบุจำนวนเงินที่มากกว่า 0";
  errors.method = form.method ? "" : "กรุณาเลือกช่องทางชำระ";
  return !Object.values(errors).some(Boolean);
};

// ─── Public API ───────────────────────────────────────
/**
 * open(payment)
 * รับ payment object จาก store มาแสดงในฟอร์มพร้อมแก้ไข
 */
const open = (payment) => {
  if (!payment) return;
  resetForm();
  Object.assign(form, {
    _id: payment.id,
    paymentNo: payment.paymentNo,
    invoiceNo: payment.invoiceNo,
    residentName: payment.residentName,
    unitCode: payment.unitCode,
    building: payment.building,
    paymentDate: payment.paymentDate,
    amount: payment.amount,
    method: payment.method,
    bankName: payment.bankName !== "-" ? payment.bankName : "",
    referenceNo: payment.referenceNo !== "-" ? payment.referenceNo : "",
    status: payment.status,
    receiptNo: payment.receiptNo !== "-" ? payment.receiptNo : "",
    note: payment.note || "",
  });
  nextTick(() => getModal().show());
};

const close = () => getModal().hide();

// ─── Submit ───────────────────────────────────────────
const handleSubmit = () => {
  if (!validate()) return;

  paymentStore.updatePayment({
    id: form._id,
    paymentDate: form.paymentDate,
    amount: form.amount,
    method: form.method,
    bankName: form.bankName || "-",
    referenceNo: form.referenceNo || "-",
    status: form.status,
    receiptNo: form.receiptNo || "-",
    note: form.note,
  });

  close();
  emit("saved");
};

defineExpose({ open, close });
</script>
<template>
  <div class="modal fade" tabindex="-1" ref="modalEl">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content rounded-4 border-0 shadow">
        <!-- Header -->
        <div class="modal-header border-0 pb-0 px-4 pt-4">
          <div>
            <h5 class="modal-title fw-bold mb-0">
              <i class="bi bi-pencil-square me-1 text-primary"></i
              >แก้ไขรายการรับชำระเงิน
            </h5>
            <div class="text-muted small mt-1">{{ form.paymentNo }}</div>
          </div>
          <button type="button" class="btn-close" @click="close"></button>
        </div>

        <!-- Body -->
        <div class="modal-body px-4 py-3">
          <!-- ข้อมูลอ้างอิง (readonly) -->
          <div class="bg-light rounded-3 p-3 mb-4">
            <div class="row g-2 small">
              <div class="col-md-4">
                <div class="text-muted">ใบแจ้งหนี้</div>
                <div class="fw-semibold">{{ form.invoiceNo }}</div>
              </div>
              <div class="col-md-4">
                <div class="text-muted">ลูกบ้าน</div>
                <div class="fw-semibold">{{ form.residentName }}</div>
              </div>
              <div class="col-md-4">
                <div class="text-muted">ยูนิต</div>
                <div class="fw-semibold">
                  {{ form.unitCode }} • {{ form.building }}
                </div>
              </div>
            </div>
          </div>

          <div class="row g-3">
            <!-- วันที่รับชำระ -->
            <div class="col-md-6">
              <label class="form-label fw-semibold">
                วันที่รับชำระ <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.paymentDate"
                type="date"
                class="form-control rounded-3"
                :class="{ 'is-invalid': errors.paymentDate }"
              />
              <div class="invalid-feedback">{{ errors.paymentDate }}</div>
            </div>

            <!-- จำนวนเงิน -->
            <div class="col-md-6">
              <label class="form-label fw-semibold">
                จำนวนเงินที่รับชำระ <span class="text-danger">*</span>
              </label>
              <input
                v-model.number="form.amount"
                type="number"
                min="0"
                class="form-control rounded-3"
                :class="{ 'is-invalid': errors.amount }"
              />
              <div class="invalid-feedback">{{ errors.amount }}</div>
            </div>

            <!-- ช่องทางชำระ -->
            <div class="col-12">
              <label class="form-label fw-semibold">
                ช่องทางชำระ <span class="text-danger">*</span>
              </label>
              <div class="row g-2">
                <div
                  v-for="m in paymentMethods"
                  :key="m.value"
                  class="col-6 col-md-3"
                >
                  <button
                    type="button"
                    class="method-btn w-100"
                    :class="{ active: form.method === m.value }"
                    @click="form.method = m.value"
                  >
                    <i
                      :class="`bi ${m.icon} d-block mb-1`"
                      style="font-size: 1.25rem"
                    ></i>
                    <span class="small">{{ m.label }}</span>
                  </button>
                </div>
              </div>
              <div v-if="errors.method" class="text-danger small mt-1">
                {{ errors.method }}
              </div>
            </div>

            <!-- ธนาคาร / ระบบ -->
            <div class="col-md-6">
              <label class="form-label fw-semibold">ธนาคาร / ระบบ</label>
              <input
                v-model="form.bankName"
                type="text"
                class="form-control rounded-3"
                placeholder="เช่น KBank, PromptPay, KTC"
              />
            </div>

            <!-- Reference No. -->
            <div class="col-md-6">
              <label class="form-label fw-semibold">Reference No.</label>
              <input
                v-model="form.referenceNo"
                type="text"
                class="form-control rounded-3"
                placeholder="เช่น TXN-123456"
              />
            </div>

            <!-- สถานะ -->
            <div class="col-md-6">
              <label class="form-label fw-semibold">สถานะรายการ</label>
              <select v-model="form.status" class="form-select rounded-3">
                <option value="completed">รับชำระสมบูรณ์</option>
                <option value="partial">ชำระบางส่วน</option>
                <option value="pending_verify">รอตรวจสอบ</option>
              </select>
            </div>

            <!-- เลขที่ใบเสร็จ -->
            <div class="col-md-6">
              <label class="form-label fw-semibold">เลขที่ใบเสร็จ</label>
              <input
                v-model="form.receiptNo"
                type="text"
                class="form-control rounded-3"
                placeholder="เช่น RC-2026-0011"
              />
            </div>

            <!-- หมายเหตุ -->
            <div class="col-12">
              <label class="form-label fw-semibold">หมายเหตุ</label>
              <textarea
                v-model="form.note"
                class="form-control rounded-3"
                rows="3"
                placeholder="บันทึกรายละเอียดเพิ่มเติม"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer border-0 px-4 pb-4 pt-2 gap-2">
          <button
            type="button"
            class="btn btn-danger border rounded-3 px-4"
            @click="close"
          >
            <i class="bi bi-x-lg me-1"></i>ยกเลิก
          </button>
          <button
            type="button"
            class="btn btn-warning rounded-3 px-4"
            @click="handleSubmit"
          >
            <i class="bi bi-floppy2-fill me-1"></i>บันทึกการแก้ไข
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.method-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0.5rem;
  border: 1.5px solid #dee2e6;
  border-radius: 0.875rem;
  background: #fff;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 80px;
}

.method-btn:hover {
  border-color: #0d6efd;
  color: #0d6efd;
  background: rgba(13, 110, 253, 0.04);
}

.method-btn.active {
  border-color: #0d6efd;
  background: rgba(13, 110, 253, 0.08);
  color: #0d6efd;
  font-weight: 600;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12);
}
</style>
